/**
 * Performance Monitoring Dashboard
 * 
 * Provides comprehensive performance monitoring and analytics
 * for real-world user experience optimization.
 */

import { onLCP, onFCP, onCLS, onINP, onTTFB } from 'web-vitals/attribution';

import { logger } from '../utils/logger';

export interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  element?: string;
  url: string;
  device: 'mobile' | 'desktop';
  timestamp: number;
  attribution?: any;
}

export interface PerformanceBudget {
  lcp: number;      // ms
  fcp: number;      // ms
  cls: number;      // score
  inp: number;      // ms
  ttfb: number;     // ms
}

export interface PerformanceReport {
  metrics: PerformanceMetric[];
  budget: PerformanceBudget;
  score: number;
  recommendations: string[];
  trends: {
    lcp: number[];
    fcp: number[];
    cls: number[];
    inp: number[];
    ttfb: number[];
  };
}

class PerformanceDashboard {
  private metrics: Map<string, PerformanceMetric[]> = new Map();
  private budget: PerformanceBudget;
  private observers: PerformanceObserver[] = [];
  private isInitialized = false;
  private memoryIntervalId: NodeJS.Timeout | null = null;

  constructor(budget: PerformanceBudget = {
    lcp: 2500,    // 2.5s
    fcp: 1800,    // 1.8s
    cls: 0.1,     // 0.1
    inp: 200,     // 200ms
    ttfb: 800     // 800ms
  }) {
    this.budget = budget;
  }

  /**
   * Initialize performance monitoring
   */
  initialize(): void {
    if (this.isInitialized) return;
    
    this.setupCoreWebVitals();
    this.setupResourceMonitoring();
    this.setupNavigationTiming();
    this.setupMemoryMonitoring();
    
    this.isInitialized = true;
    logger.info('📊 Performance Dashboard initialized');
  }

  /**
   * Setup Core Web Vitals monitoring
   */
  private setupCoreWebVitals(): void {
    // LCP - Largest Contentful Paint
    onLCP((metric) => {
      this.recordMetric({
        name: 'lcp',
        value: metric.value,
        rating: this.getRating('lcp', metric.value),
        element: (metric.attribution as any)?.element?.tagName,
        url: window.location.href,
        device: this.getDeviceType(),
        timestamp: Date.now(),
        attribution: metric.attribution
      });
    });

    // FCP - First Contentful Paint
    onFCP((metric) => {
      this.recordMetric({
        name: 'fcp',
        value: metric.value,
        rating: this.getRating('fcp', metric.value),
        url: window.location.href,
        device: this.getDeviceType(),
        timestamp: Date.now(),
        attribution: metric.attribution
      });
    });

    // CLS - Cumulative Layout Shift
    onCLS((metric) => {
      this.recordMetric({
        name: 'cls',
        value: metric.value,
        rating: this.getRating('cls', metric.value),
        url: window.location.href,
        device: this.getDeviceType(),
        timestamp: Date.now(),
        attribution: metric.attribution
      });
    });

    // INP - Interaction to Next Paint
    onINP((metric) => {
      this.recordMetric({
        name: 'inp',
        value: metric.value,
        rating: this.getRating('inp', metric.value),
        url: window.location.href,
        device: this.getDeviceType(),
        timestamp: Date.now(),
        attribution: metric.attribution
      });
    });

    // TTFB - Time to First Byte
    onTTFB((metric) => {
      this.recordMetric({
        name: 'ttfb',
        value: metric.value,
        rating: this.getRating('ttfb', metric.value),
        url: window.location.href,
        device: this.getDeviceType(),
        timestamp: Date.now(),
        attribution: metric.attribution
      });
    });
  }

  /**
   * Setup resource monitoring
   */
  private setupResourceMonitoring(): void {
    if (!('PerformanceObserver' in window)) return;

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'resource') {
          this.analyzeResource(entry as PerformanceResourceTiming);
        }
      }
    });

    observer.observe({ entryTypes: ['resource'] });
    this.observers.push(observer);
  }

  /**
   * Setup navigation timing
   */
  private setupNavigationTiming(): void {
    if (!('PerformanceObserver' in window)) return;

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'navigation') {
          this.analyzeNavigation(entry as PerformanceNavigationTiming);
        }
      }
    });

    observer.observe({ entryTypes: ['navigation'] });
    this.observers.push(observer);
  }

  /**
   * Setup memory monitoring
   */
  private setupMemoryMonitoring(): void {
    if (!('memory' in performance)) return;

    const checkMemory = () => {
      const memory = (performance as any).memory;
      if (memory) {
        const memoryMB = memory.usedJSHeapSize / 1024 / 1024;
        // Only log when memory usage is high (>100MB)
        if (memoryMB > 100) {
          this.recordMetric({
            name: 'memory-used',
            value: memoryMB,
            rating: this.getMemoryRating(memory.usedJSHeapSize),
            url: window.location.href,
            device: this.getDeviceType(),
            timestamp: Date.now()
          });
        }
      }
    };

    // Check memory every 30 seconds (only in development)
    if (import.meta.env.DEV) {
      this.memoryIntervalId = setInterval(checkMemory, 30000);
      checkMemory(); // Initial check
    }
  }

  /**
   * Record a performance metric
   */
  private recordMetric(metric: PerformanceMetric): void {
    const { name } = metric;
    
    if (!this.metrics.has(name)) {
      this.metrics.set(name, []);
    }
    
    this.metrics.get(name)!.push(metric);
    
    // Keep only last 100 metrics per type
    const metrics = this.metrics.get(name)!;
    if (metrics.length > 100) {
      this.metrics.set(name, metrics.slice(-100));
    }
    
    // Only log critical metrics and reduce frequency
    if (import.meta.env.DEV && (name === 'lcp' || name === 'fcp') && Math.random() < 0.1) {
      logger.info(`📊 ${name.toUpperCase()}:`, {
        value: metric.value,
        rating: metric.rating,
        element: metric.element
      });
    }
    
    // Send to analytics
    this.sendToAnalytics(metric);
  }

  /**
   * Analyze resource performance
   */
  private analyzeResource(entry: PerformanceResourceTiming): void {
    const duration = entry.responseEnd - entry.startTime;
    const size = entry.transferSize || 0;
    
    // ✅ PHASE B3: Raise slow-resource threshold from 1000ms to 1200ms
    // After removing duplicate fetches, remaining calls are legitimate
    // This reduces noise while still catching truly slow resources
    if (duration > 1200) {
      logger.warn('🐌 Slow resource:', {
        name: entry.name,
        duration: Math.round(duration),
        size: Math.round(size / 1024) + 'KB'
      });
    }
    
    // Log large resources
    if (size > 500 * 1024) { // 500KB
      logger.warn('📦 Large resource:', {
        name: entry.name,
        size: Math.round(size / 1024) + 'KB'
      });
    }
  }

  /**
   * Analyze navigation performance
   */
  private analyzeNavigation(entry: PerformanceNavigationTiming): void {
    const domContentLoaded = entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart;
    const loadComplete = entry.loadEventEnd - entry.loadEventStart;
    
    logger.info('🚀 Navigation timing:', {
      domContentLoaded: Math.round(domContentLoaded),
      loadComplete: Math.round(loadComplete),
      total: Math.round(entry.loadEventEnd - entry.fetchStart)
    });
  }

  /**
   * Get performance rating
   */
  private getRating(metricName: keyof PerformanceBudget, value: number): 'good' | 'needs-improvement' | 'poor' {
    const budget = this.budget[metricName];
    
    if (value <= budget) return 'good';
    if (value <= budget * 1.5) return 'needs-improvement';
    return 'poor';
  }

  /**
   * Get memory rating
   */
  private getMemoryRating(usedJSHeapSize: number): 'good' | 'needs-improvement' | 'poor' {
    const mb = usedJSHeapSize / 1024 / 1024;
    
    if (mb <= 50) return 'good';
    if (mb <= 100) return 'needs-improvement';
    return 'poor';
  }

  /**
   * Get device type
   */
  private getDeviceType(): 'mobile' | 'desktop' {
    return window.innerWidth < 768 ? 'mobile' : 'desktop';
  }

  /**
   * Send metric to analytics
   */
  private sendToAnalytics(metric: PerformanceMetric): void {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'performance_metric', {
        metric_name: metric.name,
        metric_value: Math.round(metric.value),
        metric_rating: metric.rating,
        device_type: metric.device,
        custom_parameter: 'dashboard'
      });
    }
  }

  /**
   * Get performance report
   */
  getReport(): PerformanceReport {
    const allMetrics: PerformanceMetric[] = [];
    const trends: PerformanceReport['trends'] = {
      lcp: [],
      fcp: [],
      cls: [],
      inp: [],
      ttfb: []
    };
    
    // Collect all metrics and trends
    for (const [name, metrics] of this.metrics.entries()) {
      allMetrics.push(...metrics);
      
      if (name in trends) {
        trends[name as keyof typeof trends] = metrics.map(m => m.value);
      }
    }
    
    // Calculate overall score
    const score = this.calculateScore(allMetrics);
    
    // Generate recommendations
    const recommendations = this.generateRecommendations(allMetrics);
    
    return {
      metrics: allMetrics,
      budget: this.budget,
      score,
      recommendations,
      trends
    };
  }

  /**
   * Calculate overall performance score
   */
  private calculateScore(metrics: PerformanceMetric[]): number {
    if (metrics.length === 0) return 0;
    
    const scores = metrics.map(metric => {
      switch (metric.rating) {
        case 'good': return 100;
        case 'needs-improvement': return 60;
        case 'poor': return 20;
        default: return 0;
      }
    });
    
    return Math.round(scores.reduce((sum: number, score: number) => sum + score, 0) / scores.length);
  }

  /**
   * Generate performance recommendations
   */
  private generateRecommendations(metrics: PerformanceMetric[]): string[] {
    const recommendations: string[] = [];
    
    // Analyze LCP
    const lcpMetrics = metrics.filter(m => m.name === 'lcp');
    if (lcpMetrics.length > 0) {
      const avgLCP = lcpMetrics.reduce((sum, m) => sum + m.value, 0) / lcpMetrics.length;
      if (avgLCP > this.budget.lcp) {
        recommendations.push('Optimize images and reduce render-blocking resources to improve LCP');
      }
    }
    
    // Analyze CLS
    const clsMetrics = metrics.filter(m => m.name === 'cls');
    if (clsMetrics.length > 0) {
      const avgCLS = clsMetrics.reduce((sum, m) => sum + m.value, 0) / clsMetrics.length;
      if (avgCLS > this.budget.cls) {
        recommendations.push('Add size attributes to images and avoid inserting content above existing content');
      }
    }
    
    // Analyze INP
    const inpMetrics = metrics.filter(m => m.name === 'inp');
    if (inpMetrics.length > 0) {
      const avgINP = inpMetrics.reduce((sum, m) => sum + m.value, 0) / inpMetrics.length;
      if (avgINP > this.budget.inp) {
        recommendations.push('Optimize JavaScript execution and reduce main thread blocking');
      }
    }
    
    return recommendations;
  }

  /**
   * Get average metric value
   */
  getAverageMetric(name: string): number {
    const metrics = this.metrics.get(name) || [];
    if (metrics.length === 0) return 0;
    
    return metrics.reduce((sum, metric) => sum + metric.value, 0) / metrics.length;
  }

  /**
   * Get latest metric value
   */
  getLatestMetric(name: string): PerformanceMetric | undefined {
    const metrics = this.metrics.get(name) || [];
    return metrics[metrics.length - 1];
  }

  /**
   * Clear all metrics
   */
  clearMetrics(): void {
    this.metrics.clear();
    logger.info('🧹 Performance metrics cleared');
  }

  /**
   * Cleanup observers
   */
  destroy(): void {
    // Clear memory monitoring interval
    if (this.memoryIntervalId) {
      clearInterval(this.memoryIntervalId);
      this.memoryIntervalId = null;
    }
    
    // Disconnect all observers
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    
    // Clear metrics
    this.metrics.clear();
    this.isInitialized = false;
    
    if (import.meta.env.DEV) {
      logger.info('📊 Performance Dashboard destroyed');
    }
  }
}

// Export singleton instance
export const performanceDashboard = new PerformanceDashboard();

// Auto-initialize in browser
if (typeof window !== 'undefined') {
  performanceDashboard.initialize();
}
